/**
 * A posting rule in the spec and the draft (step 2.3 of docs/plans/ledger.md),
 * and the Accounts list a money kit makes (L15).
 *
 * A record that moves money carries one rule: a pattern of the closed set
 * (L4, `ledger/patterns.ts`) and a map of the pattern's roles to the record's
 * fields, by their spec KEYS, as everything in a spec is keyed. Apply rewrites
 * each key to the id it mints (`postingMapOf`) and writes `app.posting_rule`.
 * The setup agent picks a pattern and maps fields; it never writes lines.
 *
 * A role reads one of:
 *
 *   { fieldKey }              a field of the record. On an account role a
 *                             reference to the Accounts list (L16), or a select
 *                             whose every option names its account (L45:
 *                             Cash to Cash, UPI to the bank, Credit to the
 *                             receivable). On a `lines` role, `each` maps the
 *                             roles of one row to the lines record's fields.
 *   { const }                 a number, a flag or a text.
 *   { systemKey }             an account by its system key (cash, receivable).
 *   { row }                   a row of the Accounts list, by the word its name
 *                             holds ("Rent", "Operator incentive").
 *   { self: true }            on a party role, the entry itself: a hotel's
 *                             booking is its own party (L46, step 9.6).
 *
 * `byChoice` lets a record that goes both ways pick its pattern for each entry
 * (L44); `dimensions` are the fields each line keeps (L41).
 *
 * `ruleProblems` refuses a rule the server could not post by: a pattern off
 * the set, a role the pattern does not have, a required role mapped to
 * nothing, a role that names no field of the record, a field of the wrong
 * type for its role, a choice option with no account or no pattern. Pure.
 */
import {
  checkRule,
  isQuantityMove,
  QUANTITY_MOVES,
  isLedgerAccountType,
  isLedgerSystemKey,
  isPostingPattern,
  PATTERN_ROLES,
  type LedgerAccountType,
  type PostingMap,
  type PostingPattern,
  type QuantityMove,
  type QuantityPart,
  type RoleDef,
  type RoleKind,
  type RoleSource,
} from '../ledger/patterns';
import type { ValueTypeId } from '../generated/value-types';

/** Where one role reads its value, keyed as a spec is. */
export type SpecRoleSource =
  | {
      readonly fieldKey: string;
      /** A select: each option key to the account it names. */
      readonly options?: Readonly<Record<string, SpecRoleSource>>;
      /** A `lines` field: the roles one row maps, by the lines record's field keys. */
      readonly each?: Readonly<Record<string, SpecRoleSource>>;
    }
  | { readonly const: string | number | boolean }
  | { readonly systemKey: string }
  | { readonly row: string }
  | { readonly self: true };

/**
 * The quantity a rule moves (Phase 9, D3), by field keys: `in` (came in:
 * bought, collected, made), `out` (went out: sold, used) or `loss` (wasted),
 * of the item `item` picks (a reference to the stock items list), by `qty`,
 * in the unit `unit` names (absent: the item's own unit). With `lines`, the
 * three are fields of that lines field's rows: each row moves its own item.
 */
export type SpecQuantityPart = {
  readonly move: QuantityMove;
  readonly item: string;
  readonly qty: string;
  readonly unit?: string;
  readonly lines?: string;
};

/** One option of a both-ways choice (L44): its own pattern, the roles it lays over the rule's, and the quantity it moves (null: none). */
export type SpecPostingChoice = {
  readonly pattern?: PostingPattern;
  readonly roles?: Readonly<Record<string, SpecRoleSource>>;
  readonly quantity?: SpecQuantityPart | null;
};

/** A record's posting rule, by keys. The draft holds the same shape (`SetupDraftRecord.posting`). */
export type SpecPostingRule = {
  readonly pattern: PostingPattern;
  readonly roles?: Readonly<Record<string, SpecRoleSource>>;
  readonly byChoice?: { readonly fieldKey: string; readonly options: Readonly<Record<string, SpecPostingChoice>> };
  readonly dimensions?: readonly string[];
  /** The quantity it moves (Phase 9): beside the money, or alone under the `quantity` pattern. */
  readonly quantity?: SpecQuantityPart;
};

/**
 * The list whose rows are stock items (Phase 9, D2): which field is each
 * item's base unit (a choice whose option keys are unit keys: litre, kg,
 * pcs), and, for an item sold or bought in packs, the field naming its pack
 * unit (a choice or a text: peti, crate) and the number field saying how many
 * base units one pack holds. Build puts On hand and Wastage on the list.
 */
export type SpecStockItems = {
  readonly unitFieldKey: string;
  readonly packUnitFieldKey?: string;
  readonly packSizeFieldKey?: string;
};

/**
 * The list that IS the chart of accounts (L15): each row is one account. Its
 * Type field is a select whose option keys are the five types; apply keeps
 * which option is which type in `app.record.prefs.accounts`, and every row
 * written to the list gets its `app.ledger_account` in the same transaction.
 */
export type SpecAccountsList = { readonly typeFieldKey: string };

/** What a seeded row of the Accounts list carries beside its name and type. */
export type SpecSeedAccount = { readonly systemKey?: string; readonly openItem?: boolean; readonly contra?: boolean };

/* ---------------------------------------------------------------- the shape */

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

/** A role source read off the wire, or a sentence saying what it must be. */
export const readRoleSource = (raw: unknown, depth = 0): SpecRoleSource | string => {
  if (!isObject(raw)) return 'an object with fieldKey, const, systemKey or row';
  if (typeof raw.fieldKey === 'string' && raw.fieldKey.trim() !== '') {
    const out: { fieldKey: string; options?: Record<string, SpecRoleSource>; each?: Record<string, SpecRoleSource> } = { fieldKey: raw.fieldKey.trim() };
    for (const member of ['options', 'each'] as const) {
      const held = raw[member];
      if (held === undefined || held === null) continue;
      if (!isObject(held) || depth > 1) return `${member} as an object of role sources`;
      const read: Record<string, SpecRoleSource> = {};
      for (const [key, one] of Object.entries(held)) {
        const got = readRoleSource(one, depth + 1);
        if (typeof got === 'string') return `${member}.${key} as ${got}`;
        read[key] = got;
      }
      out[member] = read;
    }
    return out;
  }
  if (typeof raw.systemKey === 'string' && raw.systemKey.trim() !== '') return { systemKey: raw.systemKey.trim() };
  if (typeof raw.row === 'string' && raw.row.trim() !== '') return { row: raw.row.trim() };
  if (typeof raw.const === 'string' || typeof raw.const === 'number' || typeof raw.const === 'boolean') return { const: raw.const };
  if (raw.self === true) return { self: true };
  return 'an object with fieldKey, const, systemKey or row';
};

/** A quantity part read off the wire, or a sentence saying what it must be. */
export const readSpecQuantityPart = (raw: unknown): SpecQuantityPart | string => {
  const wants = `{ move: ${QUANTITY_MOVES.join(' | ')}, item, qty, unit?, lines? } (field keys)`;
  if (!isObject(raw)) return wants;
  if (!isQuantityMove(raw.move)) return wants;
  const key = (member: string): string | null | undefined => {
    const held = raw[member];
    if (held === undefined || held === null) return undefined;
    return typeof held === 'string' && held.trim() !== '' ? held.trim() : null;
  };
  const item = key('item');
  const qty = key('qty');
  const unit = key('unit');
  const lines = key('lines');
  if (typeof item !== 'string' || typeof qty !== 'string' || unit === null || lines === null) return wants;
  return { move: raw.move, item, qty, ...(unit === undefined ? {} : { unit }), ...(lines === undefined ? {} : { lines }) };
};

/** A stock items mark read off the wire, or a sentence saying what it must be. */
export const readSpecStockItems = (raw: unknown): SpecStockItems | string => {
  const wants = '{ unitFieldKey, packUnitFieldKey?, packSizeFieldKey? }';
  if (!isObject(raw) || typeof raw.unitFieldKey !== 'string' || raw.unitFieldKey.trim() === '') return wants;
  const opt = (member: 'packUnitFieldKey' | 'packSizeFieldKey'): string | undefined | null => {
    const held = raw[member];
    if (held === undefined || held === null) return undefined;
    return typeof held === 'string' && held.trim() !== '' ? held.trim() : null;
  };
  const packUnit = opt('packUnitFieldKey');
  const packSize = opt('packSizeFieldKey');
  if (packUnit === null || packSize === null) return wants;
  return {
    unitFieldKey: raw.unitFieldKey.trim(),
    ...(packUnit === undefined ? {} : { packUnitFieldKey: packUnit }),
    ...(packSize === undefined ? {} : { packSizeFieldKey: packSize }),
  };
};

const readRoles = (raw: unknown): Record<string, SpecRoleSource> | string => {
  if (!isObject(raw)) return 'an object of roles';
  const out: Record<string, SpecRoleSource> = {};
  for (const [role, one] of Object.entries(raw)) {
    const got = readRoleSource(one);
    if (typeof got === 'string') return `roles.${role} as ${got}`;
    out[role] = got;
  }
  return out;
};

/**
 * A posting rule read off the wire (an op, a composed setup), or a sentence
 * saying what is wrong with its shape. What it MEANS is `ruleProblems`'.
 */
export const readSpecPostingRule = (raw: unknown): SpecPostingRule | string => {
  if (!isObject(raw)) return 'an object with a pattern and roles';
  if (!isPostingPattern(raw.pattern)) return 'a pattern of the closed set (money_in, money_out, sale, purchase, receipt, payment, charge, concession, transfer, refund, adjustment, opening_balance, bill_lines, quantity)';
  const rule: { pattern: PostingPattern; roles?: Record<string, SpecRoleSource>; byChoice?: SpecPostingRule['byChoice']; dimensions?: string[]; quantity?: SpecQuantityPart } = { pattern: raw.pattern };
  if (raw.quantity !== undefined && raw.quantity !== null) {
    const quantity = readSpecQuantityPart(raw.quantity);
    if (typeof quantity === 'string') return `quantity as ${quantity}`;
    rule.quantity = quantity;
  }
  if (raw.roles !== undefined && raw.roles !== null) {
    const roles = readRoles(raw.roles);
    if (typeof roles === 'string') return roles;
    rule.roles = roles;
  }
  if (raw.byChoice !== undefined && raw.byChoice !== null) {
    const by = raw.byChoice;
    if (!isObject(by) || typeof by.fieldKey !== 'string' || !isObject(by.options)) return 'byChoice as { fieldKey, options }';
    const options: Record<string, SpecPostingChoice> = {};
    for (const [key, one] of Object.entries(by.options)) {
      if (!isObject(one)) return `byChoice.options.${key} as { pattern, roles }`;
      if (one.pattern !== undefined && one.pattern !== null && !isPostingPattern(one.pattern)) return `byChoice.options.${key}.pattern as a pattern of the closed set`;
      const roles = one.roles === undefined || one.roles === null ? undefined : readRoles(one.roles);
      if (typeof roles === 'string') return `byChoice.options.${key}.${roles}`;
      const quantity = one.quantity === undefined ? undefined : one.quantity === null ? null : readSpecQuantityPart(one.quantity);
      if (typeof quantity === 'string') return `byChoice.options.${key}.quantity as ${quantity}`;
      options[key] = {
        ...(isPostingPattern(one.pattern) ? { pattern: one.pattern } : {}),
        ...(roles === undefined ? {} : { roles }),
        ...(quantity === undefined ? {} : { quantity }),
      };
    }
    rule.byChoice = { fieldKey: by.fieldKey, options };
  }
  if (raw.dimensions !== undefined && raw.dimensions !== null) {
    if (!Array.isArray(raw.dimensions) || raw.dimensions.some((one) => typeof one !== 'string') || raw.dimensions.length > 6) return 'dimensions as at most six field keys';
    rule.dimensions = raw.dimensions as string[];
  }
  return rule;
};

/** A seeded account read off the wire, or a sentence saying what it must be. */
export const readSpecSeedAccount = (raw: unknown): SpecSeedAccount | string => {
  if (!isObject(raw)) return 'an object with systemKey, openItem or contra';
  if (raw.systemKey !== undefined && raw.systemKey !== null && !isLedgerSystemKey(raw.systemKey)) return 'a systemKey of the known set';
  return {
    ...(isLedgerSystemKey(raw.systemKey) ? { systemKey: raw.systemKey } : {}),
    ...(raw.openItem === true ? { openItem: true } : {}),
    ...(raw.contra === true ? { contra: true } : {}),
  };
};

/* --------------------------------------------------------------- the rewrite */

/** How a rule's keys become the ids apply minted. Each answers undefined for a key it does not hold. */
export type RuleIds = {
  /** A field of the record, or with `within` of the lines record a `lines` field of it holds. */
  readonly field: (fieldKey: string, within?: string) => string | undefined;
  /** A select's option, on the record or within a lines record. */
  readonly option: (fieldKey: string, optionKey: string, within?: string) => string | undefined;
  /** An Accounts row by the word its name holds: its ledger_account id. */
  readonly row?: (word: string) => string | undefined;
};

/** Thrown by `postingMapOf` when a key resolves to nothing: the validator should have refused the rule. */
export class PostingRuleKeyError extends Error {}

const sourceOf = (source: SpecRoleSource, ids: RuleIds, within?: string): RoleSource => {
  if ('systemKey' in source) return { system_key: source.systemKey };
  if ('const' in source) return { const: source.const };
  if ('self' in source) return { self: true };
  if ('row' in source) {
    const id = ids.row?.(source.row);
    if (id === undefined) throw new PostingRuleKeyError(`the rule names the account "${source.row}", which the Accounts list does not hold.`);
    return { const: id };
  }
  const field = ids.field(source.fieldKey, within);
  if (field === undefined) throw new PostingRuleKeyError(`the rule names field "${source.fieldKey}", which the record does not declare.`);
  const options =
    source.options === undefined
      ? undefined
      : Object.fromEntries(
          Object.entries(source.options).map(([key, one]) => {
            const id = ids.option(source.fieldKey, key, within);
            if (id === undefined) throw new PostingRuleKeyError(`the rule names choice "${key}" of "${source.fieldKey}", which it does not declare.`);
            return [id, sourceOf(one, ids, within)];
          }),
        );
  const each =
    source.each === undefined
      ? undefined
      : Object.fromEntries(Object.entries(source.each).map(([role, one]) => [role, sourceOf(one, ids, source.fieldKey)]));
  return { field, ...(options === undefined ? {} : { options }), ...(each === undefined ? {} : { each }) };
};

/**
 * The rule as `app.posting_rule` stores it: every key rewritten to its id, a
 * choice's options keyed by the option ids the entry holds. Throws
 * `PostingRuleKeyError` for a key that resolves to nothing.
 */
export const postingMapOf = (rule: SpecPostingRule, ids: RuleIds): { pattern: PostingPattern; map: PostingMap } => {
  const roles = (held: Readonly<Record<string, SpecRoleSource>> | undefined): Record<string, RoleSource> | undefined =>
    held === undefined ? undefined : Object.fromEntries(Object.entries(held).map(([role, one]) => [role, sourceOf(one, ids)]));
  /** A quantity part's keys as ids; its item, qty and unit within its lines field when it names one. */
  const quantityOf = (part: SpecQuantityPart): QuantityPart => {
    const field = (key: string, within?: string): string => {
      const id = ids.field(key, within);
      if (id === undefined) throw new PostingRuleKeyError(`the rule's quantity names field "${key}", which the record does not declare.`);
      return id;
    };
    const lines = part.lines === undefined ? undefined : field(part.lines);
    return {
      move: part.move,
      item: field(part.item, part.lines),
      qty: field(part.qty, part.lines),
      ...(part.unit === undefined ? {} : { unit: field(part.unit, part.lines) }),
      ...(lines === undefined ? {} : { lines }),
    };
  };
  const map: { roles?: Record<string, RoleSource>; by_choice?: PostingMap['by_choice']; dimensions?: string[]; quantity?: QuantityPart } = {};
  const own = roles(rule.roles);
  if (own !== undefined) map.roles = own;
  if (rule.quantity !== undefined) map.quantity = quantityOf(rule.quantity);
  if (rule.byChoice !== undefined) {
    const by = rule.byChoice;
    const field = ids.field(by.fieldKey);
    if (field === undefined) throw new PostingRuleKeyError(`the rule picks its pattern by "${by.fieldKey}", which the record does not declare.`);
    map.by_choice = {
      field,
      options: Object.fromEntries(
        Object.entries(by.options).map(([key, one]) => {
          const id = ids.option(by.fieldKey, key);
          if (id === undefined) throw new PostingRuleKeyError(`the rule names choice "${key}" of "${by.fieldKey}", which it does not declare.`);
          const laid = roles(one.roles);
          return [
            id,
            {
              ...(one.pattern === undefined ? {} : { pattern: one.pattern }),
              ...(laid === undefined ? {} : { roles: laid }),
              ...(one.quantity === undefined ? {} : { quantity: one.quantity === null ? null : quantityOf(one.quantity) }),
            },
          ];
        }),
      ),
    };
  }
  if (rule.dimensions !== undefined && rule.dimensions.length > 0) {
    map.dimensions = rule.dimensions.map((key) => {
      const id = ids.field(key);
      if (id === undefined) throw new PostingRuleKeyError(`the rule keeps dimension "${key}", which the record does not declare.`);
      return id;
    });
  }
  return { pattern: rule.pattern, map };
};

/** How a stored rule's ids are read back as keys (`specRuleOf`): undefined for an id the draft does not hold. */
export type RuleKeys = {
  /** A field of the record by its id, or with `within` (the `lines` field's id) a field of its lines record. */
  readonly field: (fieldId: string, within?: string) => string | undefined;
  /** A select's option by its id. */
  readonly option: (fieldId: string, optionId: string, within?: string) => string | undefined;
  /** An account by its ledger_account id: the name of its Accounts row. */
  readonly row?: (accountId: string) => string | undefined;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * A stored rule (`app.posting_rule`) read back by keys, the way the draft and
 * the spec hold it (step 12.1 of docs/plans/ledger.md: Edit the setup and
 * Start like keep a built record's money rule). The inverse of
 * `postingMapOf`: a field id is its key, a choice's option ids their keys, a
 * constant that is an account's id the name of its Accounts row. Null when
 * anything does not read back (a field the draft does not hold, a pattern
 * the build does not know): the rule is then left as the workspace holds it.
 */
export const specRuleOf = (pattern: unknown, rawMap: unknown, keys: RuleKeys): SpecPostingRule | null => {
  if (!isPostingPattern(pattern) || !isRecord(rawMap)) return null;
  const sourceOf = (raw: unknown, within?: string): SpecRoleSource | null => {
    if (!isRecord(raw)) return null;
    if (typeof raw.system_key === 'string') return { systemKey: raw.system_key };
    if (raw.self === true) return { self: true };
    if ('const' in raw) {
      const value = raw.const;
      if (typeof value === 'string') {
        const row = keys.row?.(value);
        if (row !== undefined) return { row };
      }
      return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? { const: value } : null;
    }
    if (typeof raw.field !== 'string') return null;
    const fieldId = raw.field;
    const fieldKey = keys.field(fieldId, within);
    if (fieldKey === undefined) return null;
    let options: Record<string, SpecRoleSource> | undefined;
    if (raw.options !== undefined) {
      if (!isRecord(raw.options)) return null;
      options = {};
      for (const [optionId, one] of Object.entries(raw.options)) {
        const optionKey = keys.option(fieldId, optionId, within);
        const read = sourceOf(one, within);
        if (optionKey === undefined || read === null) return null;
        options[optionKey] = read;
      }
    }
    let each: Record<string, SpecRoleSource> | undefined;
    if (raw.each !== undefined) {
      if (!isRecord(raw.each)) return null;
      each = {};
      for (const [role, one] of Object.entries(raw.each)) {
        const read = sourceOf(one, fieldId);
        if (read === null) return null;
        each[role] = read;
      }
    }
    return { fieldKey, ...(options === undefined ? {} : { options }), ...(each === undefined ? {} : { each }) };
  };
  const rolesOf = (raw: unknown): Record<string, SpecRoleSource> | null | undefined => {
    if (raw === undefined) return undefined;
    if (!isRecord(raw)) return null;
    const out: Record<string, SpecRoleSource> = {};
    for (const [role, one] of Object.entries(raw)) {
      const read = sourceOf(one);
      if (read === null) return null;
      out[role] = read;
    }
    return out;
  };
  /** A stored quantity part by keys (Phase 9): null when a field does not read back. */
  const quantityOf = (raw: unknown): SpecQuantityPart | null => {
    if (!isRecord(raw) || !isQuantityMove(raw.move) || typeof raw.item !== 'string' || typeof raw.qty !== 'string') return null;
    const within = typeof raw.lines === 'string' ? raw.lines : undefined;
    const lines = within === undefined ? undefined : keys.field(within);
    if (within !== undefined && lines === undefined) return null;
    const item = keys.field(raw.item, within);
    const qty = keys.field(raw.qty, within);
    const unit = typeof raw.unit === 'string' ? keys.field(raw.unit, within) : undefined;
    if (item === undefined || qty === undefined || (typeof raw.unit === 'string' && unit === undefined)) return null;
    return { move: raw.move, item, qty, ...(unit === undefined ? {} : { unit }), ...(lines === undefined ? {} : { lines }) };
  };
  const roles = rolesOf(rawMap.roles);
  if (roles === null) return null;
  const quantity = rawMap.quantity === undefined ? undefined : quantityOf(rawMap.quantity);
  if (quantity === null) return null;
  let byChoice: SpecPostingRule['byChoice'];
  if (rawMap.by_choice !== undefined) {
    const by = rawMap.by_choice;
    if (!isRecord(by) || typeof by.field !== 'string' || !isRecord(by.options)) return null;
    const fieldKey = keys.field(by.field);
    if (fieldKey === undefined) return null;
    const options: Record<string, SpecPostingChoice> = {};
    for (const [optionId, one] of Object.entries(by.options)) {
      const optionKey = keys.option(by.field, optionId);
      if (optionKey === undefined || !isRecord(one)) return null;
      if (one.pattern !== undefined && !isPostingPattern(one.pattern)) return null;
      const laid = rolesOf(one.roles);
      if (laid === null) return null;
      const moved = one.quantity === undefined || one.quantity === null ? one.quantity : quantityOf(one.quantity);
      if (moved === null && one.quantity !== null) return null;
      options[optionKey] = {
        ...(one.pattern === undefined ? {} : { pattern: one.pattern as PostingPattern }),
        ...(laid === undefined ? {} : { roles: laid }),
        ...(moved === undefined ? {} : { quantity: moved }),
      };
    }
    byChoice = { fieldKey, options };
  }
  let dimensions: string[] | undefined;
  if (rawMap.dimensions !== undefined) {
    if (!Array.isArray(rawMap.dimensions)) return null;
    dimensions = [];
    for (const id of rawMap.dimensions) {
      const key = typeof id === 'string' ? keys.field(id) : undefined;
      if (key === undefined) return null;
      dimensions.push(key);
    }
  }
  return {
    pattern,
    ...(roles === undefined ? {} : { roles }),
    ...(byChoice === undefined ? {} : { byChoice }),
    ...(dimensions === undefined || dimensions.length === 0 ? {} : { dimensions }),
    ...(quantity === undefined ? {} : { quantity }),
  };
};

/* -------------------------------------------------------------- the meaning */

/** What `ruleProblems` reads of one field: the spec's and the draft's fields both fit. */
export type RuleField = {
  readonly key: string;
  readonly type: ValueTypeId | string;
  readonly options?: readonly { readonly key: string }[];
  readonly targetRecordKey?: string;
  /** A spec field's lines. */
  readonly lines?: { readonly fields: readonly RuleField[] };
  /** A draft formula's result, or a spec formula's (`spec.formula.result`). */
  readonly formula?: { readonly result?: string };
  readonly spec?: unknown;
};

/** What `ruleProblems` reads of a record. */
export type RuleRecord = {
  readonly key: string;
  readonly kind: string;
  readonly fields: readonly RuleField[];
  readonly accounts?: SpecAccountsList;
  /** On the list whose rows are stock items (Phase 9). */
  readonly stockItems?: SpecStockItems;
  /** On a list billed on a schedule (L23): only whether it has one is read here. */
  readonly chargePlan?: unknown;
  /** Its money rule: only the party role is read here, to tell who pays (L65). */
  readonly posting?: { readonly roles?: Readonly<Record<string, unknown>> } | null;
};

export type RuleProblem = { readonly code: string; readonly message: string; readonly path: string };

const resultOf = (field: RuleField): string | undefined => {
  if (field.formula?.result !== undefined) return field.formula.result;
  const spec = field.spec as { formula?: { result?: string }; rollup?: { result?: string } } | undefined;
  return spec?.formula?.result ?? spec?.rollup?.result;
};

/** Whether a field of this type may feed a role of this kind. */
const fits = (kind: RoleKind, field: RuleField): boolean => {
  switch (kind) {
    case 'amount':
    case 'percent': {
      if (field.type === 'money' || field.type === 'number') return true;
      if (field.type === 'formula' || field.type === 'rollup') {
        const result = resultOf(field);
        return result === undefined || result === 'money' || result === 'number';
      }
      return false;
    }
    case 'account':
      return field.type === 'reference' || field.type === 'select';
    case 'party':
      return field.type === 'reference';
    case 'date':
      return field.type === 'date' || field.type === 'datetime';
    case 'flag':
      return field.type === 'boolean';
    case 'text':
      return field.type !== 'lines' && field.type !== 'attachment';
    case 'lines':
      return field.type === 'lines';
  }
};

const WANTS: Record<RoleKind, string> = {
  amount: 'a money or number field',
  percent: 'a number field',
  account: 'a pick of the Accounts list, or a choice whose every option names an account',
  party: 'a pick of a list or of another record\'s entry',
  date: 'a date field',
  flag: 'a yes-or-no field',
  text: 'a field that holds words or a choice',
  lines: 'a lines field',
};

/**
 * Every problem with one record's rule, or none. `records` are the spec's
 * (or the draft's), so a pick can be checked against the Accounts list and a
 * `{ row }` against its rows (`accountRows`, the words of its rows' names,
 * lower case; absent skips that check).
 */
export const ruleProblems = (
  rule: SpecPostingRule,
  record: RuleRecord,
  records: readonly RuleRecord[],
  at: string,
  accountRows?: ReadonlySet<string>,
): RuleProblem[] => {
  const problems: RuleProblem[] = [];
  const say = (code: string, message: string, where = at): void => void problems.push({ code, message, path: where });
  if (!isPostingPattern(rule.pattern)) {
    say('RULE_PATTERN_UNKNOWN', `"${String(rule.pattern)}" is not a posting pattern.`);
    return problems;
  }
  if (record.kind !== 'record') say('RULE_ON_LIST', `"${record.key}" is a list; only a record that moves money carries a money rule.`);

  const accountsList = records.find((one) => one.accounts !== undefined);
  const checkSource = (role: string, def: RoleDef | undefined, source: SpecRoleSource, fields: readonly RuleField[], where: string): void => {
    if (def === undefined) return;
    if ('systemKey' in source) {
      if (def.kind !== 'account') say('RULE_FIELD_TYPE', `${role} reads an account, but it is ${WANTS[def.kind]}.`, where);
      else if (!isLedgerSystemKey(source.systemKey)) say('RULE_ACCOUNT_UNKNOWN', `${role} names the system key "${source.systemKey}", which no account carries.`, where);
      return;
    }
    if ('row' in source) {
      if (def.kind !== 'account') say('RULE_FIELD_TYPE', `${role} names an account, but it is ${WANTS[def.kind]}.`, where);
      else if (accountRows !== undefined && !accountRows.has(source.row.trim().toLowerCase())) say('RULE_ACCOUNT_UNKNOWN', `${role} names the account "${source.row}", which the Accounts list does not hold.`, where);
      return;
    }
    if ('const' in source) {
      if (def.kind === 'account' || def.kind === 'lines' || def.kind === 'party') say('RULE_FIELD_TYPE', `${role} cannot be a constant: it is ${WANTS[def.kind]}.`, where);
      return;
    }
    // The entry itself (L46): `checkRule` refuses it on any role but a party.
    if ('self' in source) return;
    const field = fields.find((one) => one.key === source.fieldKey);
    if (field === undefined) {
      say('RULE_FIELD_UNKNOWN', `${role} reads "${source.fieldKey}", which is no field here.`, where);
      return;
    }
    if (!fits(def.kind, field)) {
      say('RULE_FIELD_TYPE', `${role} reads "${field.key}", a ${field.type} field; it needs ${WANTS[def.kind]}.`, where);
      return;
    }
    if (def.kind === 'account' && field.type === 'reference' && accountsList !== undefined && field.targetRecordKey !== accountsList.key) {
      say('RULE_FIELD_TYPE', `${role} reads "${field.key}", which picks from "${field.targetRecordKey ?? ''}"; an account is a row of the Accounts list.`, where);
    }
    if (def.kind === 'account' && field.type === 'select') {
      const options = source.options ?? {};
      for (const option of field.options ?? []) {
        if (options[option.key] === undefined) say('RULE_CHOICE_UNMAPPED', `${role} reads the choice "${field.key}", and its option "${option.key}" names no account.`, where);
      }
      for (const [key, one] of Object.entries(options)) {
        if (!(field.options ?? []).some((option) => option.key === key)) say('RULE_CHOICE_UNKNOWN', `${role} maps "${key}", which is no option of "${field.key}".`, where);
        checkSource(role, def, one, fields, where);
      }
    }
    if (def.kind === 'lines') {
      const lineFields = field.lines?.fields ?? [];
      const each = def.each ?? {};
      const mapped = source.each ?? {};
      for (const [sub, one] of Object.entries(mapped)) {
        if (each[sub] === undefined) say('RULE_ROLE_UNKNOWN', `${role} has no "${sub}" on each line.`, where);
        else checkSource(`${role}.${sub}`, each[sub], one, lineFields, where);
      }
      for (const [sub, subDef] of Object.entries(each)) {
        if (subDef.required && subDef.default === undefined && mapped[sub] === undefined) say('RULE_ROLE_MISSING', `${role} maps no "${sub}" for each line.`, where);
      }
    }
  };

  // The roles and the required ones, through the same check the server runs on a save.
  const asMap: PostingMap = {
    ...(rule.roles === undefined ? {} : { roles: rule.roles as unknown as Record<string, RoleSource> }),
    ...(rule.byChoice === undefined
      ? {}
      : {
          by_choice: {
            field: rule.byChoice.fieldKey,
            options: Object.fromEntries(
              Object.entries(rule.byChoice.options).map(([key, one]) => [
                key,
                {
                  ...(one.pattern === undefined ? {} : { pattern: one.pattern }),
                  ...(one.roles === undefined ? {} : { roles: one.roles as unknown as Record<string, RoleSource> }),
                  ...(one.quantity === undefined ? {} : { quantity: one.quantity as QuantityPart | null }),
                },
              ]),
            ),
          },
        }),
    ...(rule.quantity === undefined ? {} : { quantity: rule.quantity as QuantityPart }),
  };
  for (const problem of checkRule(rule.pattern, asMap)) {
    const role = problem.role ?? '';
    if (problem.code === 'RULE_ROLE_MISSING') say(problem.code, `The ${rule.pattern} rule maps nothing to ${role}, which it needs.`);
    // A role the pattern has is unknown only as the entry itself (L46): a party alone may be that.
    else if (problem.code === 'RULE_ROLE_UNKNOWN' && isPostingPattern(rule.pattern) && PATTERN_ROLES[rule.pattern][role] !== undefined) say(problem.code, `${role} cannot be the entry itself: only a party can.`);
    else if (problem.code === 'RULE_ROLE_UNKNOWN') say(problem.code, `The ${rule.pattern} rule has no role "${role}".`);
    else if (problem.code === 'RULE_CHOICE_UNMAPPED') say(problem.code, `The option "${role}" of "${problem.fieldId ?? ''}" picks no pattern.`);
    else say(problem.code, `The rule cannot be posted by (${problem.code}).`);
  }

  const checkRoles = (pattern: PostingPattern, roles: Readonly<Record<string, SpecRoleSource>> | undefined, where: string): void => {
    const defs = PATTERN_ROLES[pattern];
    for (const [role, source] of Object.entries(roles ?? {})) checkSource(role, defs[role], source, record.fields, `${where}.roles[${role}]`);
  };
  if (rule.byChoice === undefined) checkRoles(rule.pattern, rule.roles, at);
  else {
    const by = rule.byChoice;
    const field = record.fields.find((one) => one.key === by.fieldKey);
    if (field === undefined || field.type !== 'select') say('RULE_FIELD_TYPE', `byChoice reads "${by.fieldKey}", which is no choice of this record.`);
    else {
      for (const option of field.options ?? []) if (by.options[option.key] === undefined) say('RULE_CHOICE_UNMAPPED', `The option "${option.key}" of "${field.key}" picks no pattern.`);
      for (const key of Object.keys(by.options)) if (!(field.options ?? []).some((option) => option.key === key)) say('RULE_CHOICE_UNKNOWN', `byChoice maps "${key}", which is no option of "${field.key}".`);
    }
    for (const [key, one] of Object.entries(by.options)) {
      if (one.pattern === undefined) continue;
      checkRoles(one.pattern, { ...(rule.roles ?? {}), ...(one.roles ?? {}) }, `${at}.byChoice[${key}]`);
    }
  }
  for (const key of rule.dimensions ?? []) {
    if (!record.fields.some((one) => one.key === key)) say('RULE_FIELD_UNKNOWN', `The rule keeps the dimension "${key}", which is no field here.`);
  }
  // The quantity it moves (Phase 9): its own, and each option's.
  if (rule.quantity !== undefined) problems.push(...quantityProblems(rule.quantity, record, records, `${at}.quantity`));
  for (const [key, one] of Object.entries(rule.byChoice?.options ?? {})) {
    if (one.quantity !== undefined && one.quantity !== null) problems.push(...quantityProblems(one.quantity, record, records, `${at}.byChoice[${key}].quantity`));
  }
  return problems;
};

/**
 * Every problem with one quantity part (Phase 9, D3): `item` a pick of a list
 * marked as stock items, `qty` a number or quantity, `unit` a choice or a
 * text, and with `lines` each of them a field of that lines field's rows.
 */
export const quantityProblems = (part: SpecQuantityPart, record: RuleRecord, records: readonly RuleRecord[], at: string): RuleProblem[] => {
  const problems: RuleProblem[] = [];
  const say = (code: string, message: string): void => void problems.push({ code, message, path: at });
  let fields = record.fields;
  if (part.lines !== undefined) {
    const lines = record.fields.find((one) => one.key === part.lines);
    if (lines === undefined || lines.type !== 'lines') {
      say('RULE_FIELD_TYPE', `The quantity reads the rows of "${part.lines}", which is no lines field here.`);
      return problems;
    }
    fields = lines.lines?.fields ?? [];
  }
  const fieldOf = (key: string): RuleField | undefined => fields.find((one) => one.key === key);
  const item = fieldOf(part.item);
  if (item === undefined) say('RULE_FIELD_UNKNOWN', `The quantity names its item by "${part.item}", which is no field here.`);
  else if (item.type !== 'reference') say('RULE_FIELD_TYPE', `The quantity names its item by "${item.key}", a ${item.type} field; it needs a pick of the stock items list.`);
  else {
    const target = records.find((one) => one.key === item.targetRecordKey);
    if (target?.stockItems === undefined) say('RULE_STOCK_LIST', `"${item.key}" picks from "${item.targetRecordKey ?? ''}", which is not marked as a list of stock items.`);
  }
  const qty = fieldOf(part.qty);
  if (qty === undefined) say('RULE_FIELD_UNKNOWN', `The quantity reads how much from "${part.qty}", which is no field here.`);
  else if (!['number', 'quantity'].includes(qty.type) && !((qty.type === 'formula' || qty.type === 'rollup') && ['number', 'quantity', undefined].includes(resultOf(qty)))) {
    say('RULE_FIELD_TYPE', `The quantity reads how much from "${qty.key}", a ${qty.type} field; it needs a number or a quantity.`);
  }
  if (part.unit !== undefined) {
    const unit = fieldOf(part.unit);
    if (unit === undefined) say('RULE_FIELD_UNKNOWN', `The quantity reads its unit from "${part.unit}", which is no field here.`);
    else if (unit.type !== 'select' && unit.type !== 'text') say('RULE_FIELD_TYPE', `The quantity reads its unit from "${unit.key}", a ${unit.type} field; it needs a choice of units or a text.`);
  }
  return problems;
};

/**
 * Every problem with a stock items mark (Phase 9, D2): on a list, its base
 * unit a choice whose options are unit keys, its pack unit a choice or a
 * text, and its pack size a number, each a field of the list.
 */
export const stockItemsProblems = (mark: SpecStockItems, record: RuleRecord, at: string): RuleProblem[] => {
  const problems: RuleProblem[] = [];
  const say = (code: string, message: string): void => void problems.push({ code, message, path: at });
  if (record.kind !== 'directory') say('STOCK_ITEMS_NOT_LIST', 'Stock items are the rows of a list.');
  const unit = record.fields.find((one) => one.key === mark.unitFieldKey);
  if (unit === undefined || unit.type !== 'select') say('STOCK_ITEMS_UNIT', `The stock items name their unit in "${mark.unitFieldKey}", which must be a choice of units (litre, kg, pcs).`);
  if (mark.packUnitFieldKey !== undefined) {
    const pack = record.fields.find((one) => one.key === mark.packUnitFieldKey);
    if (pack === undefined || (pack.type !== 'select' && pack.type !== 'text')) say('STOCK_ITEMS_PACK', `The pack unit "${mark.packUnitFieldKey}" must be a choice or a text field of the list.`);
  }
  if (mark.packSizeFieldKey !== undefined) {
    const size = record.fields.find((one) => one.key === mark.packSizeFieldKey);
    if (size === undefined || (size.type !== 'number' && size.type !== 'quantity')) say('STOCK_ITEMS_PACK', `The pack size "${mark.packSizeFieldKey}" must be a number field of the list.`);
  }
  if ((mark.packUnitFieldKey === undefined) !== (mark.packSizeFieldKey === undefined)) say('STOCK_ITEMS_PACK', 'A pack names both its unit and how many of the item\'s own unit it holds.');
  return problems;
};

/**
 * Every problem with an Accounts list (L15): a directory, its Type a select
 * whose options are the five types and nothing else, and each seeded row a
 * known system key held once.
 */
export const accountsListProblems = (
  record: RuleRecord,
  seedAccounts: readonly (SpecSeedAccount | null | undefined)[],
  at: string,
): RuleProblem[] => {
  const problems: RuleProblem[] = [];
  const say = (code: string, message: string): void => void problems.push({ code, message, path: at });
  if (record.kind !== 'directory') say('ACCOUNTS_NOT_LIST', 'The Accounts list is a list.');
  const typeKey = record.accounts?.typeFieldKey;
  const type = record.fields.find((one) => one.key === typeKey);
  if (type === undefined || type.type !== 'select') say('ACCOUNTS_TYPE_FIELD', 'The Accounts list names its Type field, a choice.');
  else {
    const keys = (type.options ?? []).map((option) => option.key);
    if (keys.some((key) => !isLedgerAccountType(key)) || new Set(keys).size !== keys.length) say('ACCOUNTS_TYPE_OPTIONS', 'The Type field offers the five types only: asset, liability, equity, income, expense.');
  }
  const seen = new Set<string>();
  for (const one of seedAccounts) {
    if (one?.systemKey === undefined) continue;
    if (!isLedgerSystemKey(one.systemKey)) say('ACCOUNTS_SYSTEM_KEY', `"${one.systemKey}" is no system key.`);
    if (seen.has(one.systemKey)) say('ACCOUNTS_SYSTEM_KEY_DUP', `Two rows carry the system key "${one.systemKey}".`);
    seen.add(one.systemKey);
  }
  return problems;
};

/** The type a Type option key names, or null. */
export const accountTypeOf = (optionKey: string): LedgerAccountType | null => (isLedgerAccountType(optionKey) ? optionKey : null);
